const TABLIST_SELECTOR = '.drawer-menu__tabs[role="tablist"]';
const TAB_SELECTOR = '[role="tab"]';
const ENHANCED_CLASS = 'theme-tablist--with-indicator';
const INDICATOR_CLASS = 'theme-tab-indicator';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class TabIndicatorController {
  constructor(tablist) {
    this.tablist = tablist;
    this.indicator = null;
    this.rafId = 0;
    this.mutationObserver = null;
    this.resizeObserver = null;
    this.observedTabs = new Set();
    this.boundScheduleUpdate = this.scheduleUpdate.bind(this);
  }

  init() {
    if (!(this.tablist instanceof HTMLElement)) return false;

    this.tablist.classList.add(ENHANCED_CLASS);

    const existingIndicator = this.tablist.querySelector(`:scope > .${INDICATOR_CLASS}`);
    if (existingIndicator instanceof HTMLElement) {
      this.indicator = existingIndicator;
    } else {
      this.indicator = document.createElement('span');
      this.indicator.className = INDICATOR_CLASS;
      this.indicator.setAttribute('aria-hidden', 'true');
      this.tablist.appendChild(this.indicator);
    }

    this.tablist.addEventListener('click', this.boundScheduleUpdate);
    this.tablist.addEventListener('keydown', this.boundScheduleUpdate);
    this.tablist.addEventListener('scroll', this.boundScheduleUpdate, { passive: true });
    window.addEventListener('resize', this.boundScheduleUpdate, { passive: true });

    this.mutationObserver = new MutationObserver(this.boundScheduleUpdate);
    this.mutationObserver.observe(this.tablist, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-selected', 'hidden', 'style']
    });

    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(this.boundScheduleUpdate);
      this.resizeObserver.observe(this.tablist);
      this.refreshObservedTabs();
    }

    this.scheduleUpdate();
    return true;
  }

  destroy() {
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    this.tablist.removeEventListener('click', this.boundScheduleUpdate);
    this.tablist.removeEventListener('keydown', this.boundScheduleUpdate);
    this.tablist.removeEventListener('scroll', this.boundScheduleUpdate);
    window.removeEventListener('resize', this.boundScheduleUpdate);

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.resizeObserver) {
      this.observedTabs.forEach((tab) => this.resizeObserver.unobserve(tab));
      this.observedTabs.clear();
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.indicator?.remove();
    this.tablist.classList.remove(ENHANCED_CLASS);
  }

  getTabs() {
    return Array.from(this.tablist.querySelectorAll(TAB_SELECTOR)).filter((tab) => tab instanceof HTMLElement);
  }

  refreshObservedTabs() {
    if (!this.resizeObserver) return;

    this.observedTabs.forEach((tab) => this.resizeObserver.unobserve(tab));
    this.observedTabs.clear();

    this.getTabs().forEach((tab) => {
      this.resizeObserver.observe(tab);
      this.observedTabs.add(tab);
    });
  }

  getActiveTab() {
    const tabs = this.getTabs().filter((tab) => !tab.hidden);
    if (!tabs.length) return null;

    const isVisible = (tab) => tab.offsetParent !== null;

    return (
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true' && isVisible(tab)) ||
      tabs.find((tab) => tab.classList.contains('is-active') && isVisible(tab)) ||
      tabs.find((tab) => isVisible(tab)) ||
      null
    );
  }

  scheduleUpdate() {
    if (this.rafId) return;

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = 0;
      this.refreshObservedTabs();
      this.update();
    });
  }

  update() {
    if (!(this.indicator instanceof HTMLElement)) return;

    const activeTab = this.getActiveTab();
    if (!(activeTab instanceof HTMLElement) || this.tablist.offsetWidth <= 0) {
      this.indicator.style.opacity = '0';
      return;
    }

    const x = Math.round(activeTab.offsetLeft);
    const width = Math.round(activeTab.offsetWidth);

    this.indicator.style.transform = `translate3d(${x}px, 0, 0)`;
    this.indicator.style.width = `${width}px`;
    this.indicator.style.opacity = width > 0 ? '1' : '0';

    if (prefersReducedMotion()) {
      this.indicator.style.transition = 'none';
    } else {
      this.indicator.style.removeProperty('transition');
    }
  }
}

const controllers = new WeakMap();

const mountInScope = (scope = document) => {
  scope.querySelectorAll(TABLIST_SELECTOR).forEach((tablist) => {
    if (controllers.has(tablist)) return;

    const controller = new TabIndicatorController(tablist);
    if (controller.init()) {
      controllers.set(tablist, controller);
    }
  });
};

const unmountInScope = (scope) => {
  if (!scope) return;

  scope.querySelectorAll(TABLIST_SELECTOR).forEach((tablist) => {
    const controller = controllers.get(tablist);
    if (!controller) return;
    controller.destroy();
    controllers.delete(tablist);
  });
};

const init = () => {
  mountInScope(document);

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event.target.closest('.shopify-section') || event.target;
    unmountInScope(sectionRoot);
    mountInScope(sectionRoot);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionRoot = event.target.closest('.shopify-section') || event.target;
    unmountInScope(sectionRoot);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
